import { useEffect, useMemo, useState } from "react";
import { useStoreData } from "@/store/useStoreData";
import { readContextPartyId, readLoggedInPartyId } from "@/store/api/partyContext";
import { createOrderForProduct } from "@/store/api/order";
import { addProductToCart } from "@/store/api/cart";
import { claimBundleCoupons } from "@/store/api/bundleSync";
import { getActiveCouponForCategory, isFreeForViewer, promoForPurchase, safeAvailable } from "@/store/coupons";
import { useToast } from "@/store/components/ToastProvider";
import { useConfirm } from "@/store/components/ConfirmProvider";
import StoreHeader, { SlimHeader } from "@/store/components/Header";
import SearchBox from "@/store/components/SearchBox";
import ProductGrid from "@/store/components/ProductGrid";
import CategoryFilter from "@/store/components/CategoryFilter";
import { hasEligiblePurchases, matchesActiveFilters, matchesPurchasedFilter } from "@/store/categoryFilter";
import ProductDetail from "@/store/components/ProductDetail";
import HistoryModal from "@/store/components/HistoryModal";
import ProductQuickView from "@/store/components/ProductQuickView";
import LoadingState from "@/store/components/LoadingState";
import ErrorState from "@/store/components/ErrorState";
import type { Product } from "@/store/types";

function matchesQuery(product: Product, qLower: string): boolean {
  if (!qLower) return true;
  return (
    (product.Title ?? "").toLowerCase().includes(qLower) ||
    (product.ProductCode ?? "").toLowerCase().includes(qLower) ||
    (product.Description ?? "").toLowerCase().includes(qLower)
  );
}

export default function Store() {
  const data = useStoreData();
  const showToast = useToast();
  const confirm = useConfirm();

  const partyId = readContextPartyId();
  const loggedInUserId = readLoggedInPartyId();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [quickView, setQuickView] = useState<Product | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [processingCode, setProcessingCode] = useState<string | null>(null);
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const [bundleFocus, setBundleFocus] = useState<string[]>([]);
  const [purchasedOnly, setPurchasedOnly] = useState(false);
  const [claimBundleBusy, setClaimBundleBusy] = useState(false);

  // Debounce the search input by 300ms so filtering doesn't run on every keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(id);
  }, [query]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.toLowerCase();
    return data.products.filter(
      (p) =>
        matchesQuery(p, q) &&
        matchesActiveFilters(p, activeCategories, bundleFocus) &&
        matchesPurchasedFilter(p, purchasedOnly, data.purchasedCodes)
    );
  }, [data.products, debouncedQuery, activeCategories, bundleFocus, purchasedOnly, data.purchasedCodes]);

  async function handlePurchase(product: Product) {
    const category = product.Category;
    if (isFreeForViewer(product, data.isMember)) {
      showToast("error", "This product is free — no coupon is needed.");
      return;
    }
    const coupon = getActiveCouponForCategory(data.coupons, category);
    if (!coupon) {
      showToast("error", "No coupon is available for this product.");
      return;
    }
    const promo = promoForPurchase(coupon, category);
    if (!promo) {
      showToast("error", "No promotion is configured for this category.");
      return;
    }
    if (!partyId) {
      showToast("error", "Could not determine your account. Please sign in and try again.");
      return;
    }

    const usesAfter = safeAvailable(coupon) - 1;
    const confirmed = await confirm({
      title: "Confirm Purchase",
      confirmLabel: "Confirm",
      body: (
        <>
          Are you sure you want to purchase <strong>{product.Title}</strong> using a coupon?
          <br />
          <br />
          You will have{" "}
          <strong>
            {usesAfter} use{usesAfter !== 1 ? "s" : ""}
          </strong>{" "}
          remaining after this purchase.
        </>
      ),
    });
    if (!confirmed) return;

    setBusy(true);
    setProcessingCode(product.ProductCode);
    try {
      const result = await createOrderForProduct(partyId, product.ProductCode, coupon, promo);
      if (result.result) {
        showToast("success", "Order placed! " + result.message);
        await data.refetch();
      } else {
        showToast("error", result.message);
      }
    } finally {
      setBusy(false);
      setProcessingCode(null);
    }
  }

  async function handleAddToCart(product: Product) {
    if (!partyId) {
      showToast("error", "Could not determine your account. Please sign in and try again.");
      return;
    }
    const confirmed = await confirm({
      title: "Add to Cart",
      confirmLabel: "Add to Cart",
      body: (
        <>
          Are you sure you want to add <strong>{product.Title}</strong> to your cart?
        </>
      ),
    });
    if (!confirmed) return;

    setBusy(true);
    setProcessingCode(product.ProductCode);
    try {
      const result = await addProductToCart(partyId, loggedInUserId, product.ProductCode);
      if (result.success) {
        showToast("success", `${product.Title} added to your cart.`, { showCartLink: true });
      } else {
        showToast("error", result.message ?? "Failed to add to cart.");
      }
    } finally {
      setBusy(false);
      setProcessingCode(null);
    }
  }

  async function handleClaimBundleCoupons() {
    if (!partyId) {
      showToast("error", "Could not determine your account. Please sign in and try again.");
      return;
    }
    const rows = data.unclaimedBundlePurchases;
    const confirmed = await confirm({
      title: "Claim Bundle Coupon(s)",
      confirmLabel: "Claim",
      body: (
        <>
          You have <strong>{rows.length}</strong> bundle purchase{rows.length !== 1 ? "s" : ""} from
          today ready to claim. Claim your coupon{rows.length !== 1 ? "s" : ""} now?
        </>
      ),
    });
    if (!confirmed) return;

    setClaimBundleBusy(true);
    try {
      const { added, failed } = await claimBundleCoupons(partyId, rows);
      if (failed === 0) {
        showToast("success", `${added} coupon${added !== 1 ? "s" : ""} added.`);
      } else {
        showToast(
          "error",
          `${added} coupon${added !== 1 ? "s" : ""} added, ${failed} failed — please try again.`
        );
      }
      await data.refetch();
    } finally {
      setClaimBundleBusy(false);
    }
  }

  function toggleCategory(id: string) {
    setActiveCategories((prev) => {
      const has = prev.includes(id);
      const next = has ? prev.filter((c) => c !== id) : [...prev, id];
      if (!next.includes("BUNDLE")) setBundleFocus([]);
      return next;
    });
  }

  function toggleBundleFocus(id: string) {
    setBundleFocus((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }

  function clearCategoryFilters() {
    setActiveCategories([]);
    setBundleFocus([]);
    setPurchasedOnly(false);
  }

  function togglePurchasedOnly() {
    setPurchasedOnly((v) => !v);
  }

  if (data.loading) return <LoadingState />;
  if (data.error) return <ErrorState message={data.error} showBack={data.isFiltered} />;

  // Single-product detail layout.
  if (data.singleMode && data.products.length === 1) {
    const product = data.products[0];
    return (
      <>
        <SlimHeader />
        <ProductDetail
          product={product}
          isPurchased={data.purchasedCodes.has(product.ProductCode)}
          isMember={data.isMember}
          coupons={data.coupons}
          busy={busy}
          processingCode={processingCode}
          onPurchase={handlePurchase}
          onAddToCart={handleAddToCart}
        />
      </>
    );
  }

  const showSearch = data.products.length > 1;

  return (
    <>
      <StoreHeader
        bundlesPurchased={data.history.length}
        stats={data.stats}
        coupons={data.coupons}
        activeCoupon={data.activeCoupon}
        isFiltered={data.isFiltered}
        isLoggedIn={data.isLoggedIn}
        onOpenHistory={() => setHistoryOpen(true)}
      />
      {showSearch ? <SearchBox value={query} onChange={setQuery} /> : null}
      <CategoryFilter
        activeCategories={activeCategories}
        bundleFocus={bundleFocus}
        onToggleCategory={toggleCategory}
        onToggleBundleFocus={toggleBundleFocus}
        onClearAll={clearCategoryFilters}
        purchasedOnly={purchasedOnly}
        onTogglePurchasedOnly={togglePurchasedOnly}
        showPurchasedPill={hasEligiblePurchases(data.purchasedCodes, data.products)}
        showClaimBundlePill={data.unclaimedBundlePurchases.length > 0}
        claimBundleBusy={claimBundleBusy}
        onClaimBundleCoupons={handleClaimBundleCoupons}
      />
      <ProductGrid
        products={filtered}
        purchasedCodes={data.purchasedCodes}
        isMember={data.isMember}
        coupons={data.coupons}
        busy={busy}
        processingCode={processingCode}
        onPurchase={handlePurchase}
        onAddToCart={handleAddToCart}
        onViewDetails={setQuickView}
      />

      {historyOpen ? <HistoryModal history={data.history} onClose={() => setHistoryOpen(false)} /> : null}
      {quickView ? (
        <ProductQuickView
          product={quickView}
          isPurchased={data.purchasedCodes.has(quickView.ProductCode)}
          isMember={data.isMember}
          coupons={data.coupons}
          busy={busy}
          processingCode={processingCode}
          onClose={() => setQuickView(null)}
          onPurchase={handlePurchase}
          onAddToCart={handleAddToCart}
        />
      ) : null}
    </>
  );
}
